import { zodResolver } from '@hookform/resolvers/zod';
import {
  useGetApiAuthApiKeys,
  usePostApiAuthApiKeys,
  useDeleteApiAuthApiKeysId,
  usePatchApiAuthApiKeysId,
  getGetApiAuthApiKeysQueryKey,
  type AuthAPIKey,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  Check,
  Clock,
  AlertCircle,
  ArrowLeft,
  Plus,
  Key,
  X,
  Pencil,
  Trash2,
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { Modal, Button, cn } from '@/components/ui';

const createKeySchema = z
  .object({
    name: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or less'),
    expiresIn: z.enum(['never', '30days', '90days', '1year', 'custom']),
    customDays: z.coerce.number().min(1).max(3650).optional(),
  })
  .refine(
    (data) => {
      if (data.expiresIn === 'custom') {
        return data.customDays !== undefined && data.customDays >= 1;
      }
      return true;
    },
    {
      message: 'Please enter a valid number of days (1-3650)',
      path: ['customDays'],
    }
  );

const EXPIRATION_OPTIONS = [
  { value: 'never', label: 'Never expires', description: 'Key remains valid indefinitely' },
  { value: '30days', label: '30 days', description: 'Expires in about 1 month' },
  { value: '90days', label: '90 days', description: 'Expires in about 3 months' },
  { value: '1year', label: '1 year', description: 'Expires in 365 days' },
  { value: 'custom', label: 'Custom...', description: 'Choose your own duration' },
] as const;

const renameKeySchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or less'),
});

type CreateKeyFormData = z.infer<typeof createKeySchema>;
type RenameKeyFormData = z.infer<typeof renameKeySchema>;

/**
 * API Keys management page - create, view, rename, and revoke API keys
 */
export function ApiKeysPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useGetApiAuthApiKeys();
  const createMutation = usePostApiAuthApiKeys();
  const deleteMutation = useDeleteApiAuthApiKeysId();
  const updateMutation = usePatchApiAuthApiKeysId();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [keyToDelete, setKeyToDelete] = useState<AuthAPIKey | null>(null);
  const [keyToRename, setKeyToRename] = useState<AuthAPIKey | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const invalidateApiKeys = () => {
    queryClient.invalidateQueries({ queryKey: getGetApiAuthApiKeysQueryKey() });
  };

  const handleCreateKey = async (formData: CreateKeyFormData) => {
    let expiresInSeconds: number | undefined;

    switch (formData.expiresIn) {
      case 'never':
        expiresInSeconds = undefined;
        break;
      case '30days':
        expiresInSeconds = 30 * 24 * 60 * 60;
        break;
      case '90days':
        expiresInSeconds = 90 * 24 * 60 * 60;
        break;
      case '1year':
        expiresInSeconds = 365 * 24 * 60 * 60;
        break;
      case 'custom':
        expiresInSeconds = (formData.customDays ?? 30) * 24 * 60 * 60;
        break;
    }

    try {
      const result = await createMutation.mutateAsync({
        data: {
          name: formData.name,
          expiresIn: expiresInSeconds,
        },
      });
      if (result.apiKey) {
        setNewlyCreatedKey(result.apiKey);
      }
      invalidateApiKeys();
      setShowCreateModal(false);
    } catch {
      // Error is handled by the mutation state
    }
  };

  const handleDeleteKey = async () => {
    if (!keyToDelete?.id) return;
    try {
      await deleteMutation.mutateAsync({ id: keyToDelete.id });
      invalidateApiKeys();
      setKeyToDelete(null);
    } catch {
      // Error is handled by the mutation state
    }
  };

  const handleRenameKey = async (formData: RenameKeyFormData) => {
    if (!keyToRename?.id) return;
    try {
      await updateMutation.mutateAsync({
        id: keyToRename.id,
        data: { name: formData.name },
      });
      invalidateApiKeys();
      setKeyToRename(null);
    } catch {
      // Error is handled by the mutation state
    }
  };

  const copyToClipboard = async (text: string, id?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      if (id) {
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
      }
    } catch {
      // Clipboard API failed
    }
  };

  const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return 'Never';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const isExpired = (dateStr: string | undefined): boolean => {
    if (!dateStr) return false;
    try {
      return new Date(dateStr) < new Date();
    } catch {
      return false;
    }
  };

  const activeKeys = data?.apiKeys?.filter((key) => !key.isRevoked) ?? [];
  const revokedKeys = data?.apiKeys?.filter((key) => key.isRevoked) ?? [];

  return (
    <div className="space-y-8">
      {/* Header with back link */}
      <div>
        <Link
          to={ROUTES.SETTINGS}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              API Keys
            </h1>
            <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
              Manage API keys for programmatic access
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Create Key
          </button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-medium text-red-400">Unable to fetch API keys</h3>
              <p className="mt-1 text-sm text-red-400/70">
                There was an error loading your API keys.
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
          >
            Retry
          </button>
        </div>
      )}

      {/* Newly created key alert */}
      {newlyCreatedKey && (
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/20">
              <Key className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-medium text-emerald-400">API Key Created</h3>
              <p className="mt-1 text-sm text-emerald-400/70">
                Copy your API key now. You won&apos;t be able to see it again.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <code className="flex-1 rounded-lg bg-gray-100 px-3 py-2 font-mono text-sm text-gray-900 dark:bg-zinc-900 dark:text-white">
                  {newlyCreatedKey}
                </code>
                <button
                  onClick={() => copyToClipboard(newlyCreatedKey, 'new')}
                  className="shrink-0 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm font-medium text-emerald-400 transition-colors hover:bg-emerald-500/20"
                >
                  {copiedId === 'new' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <button
              onClick={() => setNewlyCreatedKey(null)}
              className="shrink-0 text-emerald-400/50 transition-colors hover:text-emerald-400"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* API Keys data */}
      {!isLoading && !isError && (
        <>
          {/* Active keys */}
          <div>
            <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
              Active Keys ({activeKeys.length})
            </h2>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
              {activeKeys.length > 0 ? (
                activeKeys.map((key, index) => (
                  <div
                    key={key.id}
                    className={`p-4 ${index !== 0 ? 'border-t border-gray-200 dark:border-white/[0.06]' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[15px] font-medium text-gray-900 dark:text-white">
                            {key.name}
                          </span>
                          {isExpired(key.expiresAt) && (
                            <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-xs font-medium text-amber-500 dark:text-amber-400">
                              Expired
                            </span>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-zinc-500">
                          <span>Created {formatDate(key.createdAt)}</span>
                          <span>
                            Expires{' '}
                            {key.expiresAt
                              ? isExpired(key.expiresAt)
                                ? formatDate(key.expiresAt)
                                : formatDate(key.expiresAt)
                              : 'Never'}
                          </span>
                          {key.lastUsedAt && <span>Last used {formatDate(key.lastUsedAt)}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setKeyToRename(key)}
                          className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-500 dark:hover:bg-white/[0.03] dark:hover:text-zinc-300"
                          title="Rename"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setKeyToDelete(key)}
                          className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-red-500/10 hover:text-red-500 dark:text-zinc-500 dark:hover:text-red-400"
                          title="Revoke"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center">
                  <Key className="mx-auto h-8 w-8 text-gray-400 dark:text-zinc-600" />
                  <p className="mt-3 text-sm text-gray-500 dark:text-zinc-500">
                    No active API keys
                  </p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="mt-4 text-sm font-medium text-blue-500 transition-colors hover:text-blue-400"
                  >
                    Create your first API key
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Revoked keys */}
          {revokedKeys.length > 0 && (
            <div>
              <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
                Revoked Keys ({revokedKeys.length})
              </h2>
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
                {revokedKeys.map((key, index) => (
                  <div
                    key={key.id}
                    className={`p-4 opacity-60 ${index !== 0 ? 'border-t border-gray-200 dark:border-white/[0.06]' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[15px] font-medium text-gray-900 line-through dark:text-white">
                        {key.name}
                      </span>
                      <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-xs font-medium text-red-500 dark:text-red-400">
                        Revoked
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-gray-500 dark:text-zinc-500">
                      Created {formatDate(key.createdAt)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Key Modal */}
      {showCreateModal && (
        <CreateKeyModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateKey}
          isLoading={createMutation.isPending}
          error={createMutation.error}
        />
      )}

      {/* Rename Key Modal */}
      {keyToRename && (
        <RenameKeyModal
          apiKey={keyToRename}
          onClose={() => setKeyToRename(null)}
          onSubmit={handleRenameKey}
          isLoading={updateMutation.isPending}
          error={updateMutation.error}
        />
      )}

      {/* Delete Confirmation Modal */}
      {keyToDelete && (
        <DeleteKeyModal
          apiKey={keyToDelete}
          onClose={() => setKeyToDelete(null)}
          onConfirm={handleDeleteKey}
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

// Form Input component for consistent styling
interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
}

function FormInput({ label, error, className, ...props }: FormInputProps) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
        {label}
      </label>
      <input
        className={cn(
          'w-full rounded-lg border px-3 py-2.5 text-sm transition-all',
          'bg-white text-gray-900 placeholder-gray-400 dark:bg-[#111113] dark:text-white dark:placeholder-zinc-500',
          'focus:outline-none focus:ring-1',
          error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-200 focus:border-blue-500 focus:ring-blue-500 dark:border-white/[0.08]',
          props.disabled && 'cursor-not-allowed opacity-50'
        )}
        {...props}
      />
      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}

// Dropdown Select component
interface DropdownSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string; description?: string }[];
  error?: string;
  icon?: React.ReactNode;
}

function DropdownSelect({ label, value, onChange, options, error, icon }: DropdownSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-all',
          'bg-white dark:bg-[#111113]',
          error
            ? 'border-red-500 focus:ring-red-500'
            : 'border-gray-200 hover:border-gray-300 dark:border-white/[0.08] dark:hover:border-white/[0.12]',
          isOpen && !error && 'border-blue-500 ring-1 ring-blue-500'
        )}
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-gray-900 dark:text-white">{selectedOption?.label}</span>
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-gray-400 transition-transform dark:text-zinc-500',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1.5 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-white/[0.08] dark:bg-[#1c1c1f]">
          <div className="max-h-56 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={cn(
                  'flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors',
                  'hover:bg-gray-50 dark:hover:bg-white/[0.04]',
                  value === option.value && 'bg-blue-50 dark:bg-blue-500/10'
                )}
              >
                <div>
                  <div
                    className={cn(
                      'text-gray-900 dark:text-white',
                      value === option.value && 'text-blue-600 dark:text-blue-400'
                    )}
                  >
                    {option.label}
                  </div>
                  {option.description && (
                    <div className="text-xs text-gray-500 dark:text-zinc-500">
                      {option.description}
                    </div>
                  )}
                </div>
                {value === option.value && (
                  <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}

// Create Key Modal Component
function CreateKeyModal({
  onClose,
  onSubmit,
  isLoading,
  error,
}: {
  onClose: () => void;
  onSubmit: (data: CreateKeyFormData) => Promise<void>;
  isLoading: boolean;
  error: unknown;
}) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateKeyFormData>({
    resolver: zodResolver(createKeySchema),
    defaultValues: {
      name: '',
      expiresIn: 'never',
      customDays: 30,
    },
  });

  const selectedExpiration = watch('expiresIn');

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={onClose}>
        Cancel
      </Button>
      <Button type="submit" form="create-key-form" loading={isLoading}>
        Create Key
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Create API Key"
      subtitle="Create a new key for programmatic access"
      size="md"
      footer={footerContent}
    >
      <form id="create-key-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <FormInput
          label="Name"
          placeholder="e.g., Production Server"
          error={errors.name?.message}
          {...register('name')}
        />

        <DropdownSelect
          label="Expiration"
          value={selectedExpiration}
          onChange={(value) =>
            setValue('expiresIn', value as 'never' | '30days' | '90days' | '1year' | 'custom')
          }
          options={EXPIRATION_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label,
            description: opt.description,
          }))}
          icon={<Clock className="h-4 w-4 text-gray-400 dark:text-zinc-500" />}
        />

        {selectedExpiration === 'custom' && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-white/[0.06] dark:bg-[#111113]">
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
              Number of days
            </label>
            <div className="flex items-center gap-3">
              <input
                {...register('customDays')}
                type="number"
                min={1}
                max={3650}
                placeholder="30"
                className="block w-24 rounded-lg border border-gray-200 bg-white px-3 py-2 text-center text-sm text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white dark:placeholder-zinc-500"
              />
              <span className="text-sm text-gray-600 dark:text-zinc-400">days from now</span>
            </div>
            {errors.customDays && (
              <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
                <AlertCircle className="h-3.5 w-3.5" />
                {errors.customDays.message}
              </p>
            )}
          </div>
        )}

        {error !== null && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            <AlertCircle className="h-4 w-4" />
            Failed to create API key. Please try again.
          </div>
        )}
      </form>
    </Modal>
  );
}

// Rename Key Modal Component
function RenameKeyModal({
  apiKey,
  onClose,
  onSubmit,
  isLoading,
  error,
}: {
  apiKey: AuthAPIKey;
  onClose: () => void;
  onSubmit: (data: RenameKeyFormData) => Promise<void>;
  isLoading: boolean;
  error: unknown;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RenameKeyFormData>({
    resolver: zodResolver(renameKeySchema),
    defaultValues: {
      name: apiKey.name ?? '',
    },
  });

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={onClose}>
        Cancel
      </Button>
      <Button type="submit" form="rename-key-form" loading={isLoading}>
        Save
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Rename API Key"
      subtitle="Update the display name for this key"
      size="md"
      footer={footerContent}
    >
      <form id="rename-key-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <FormInput label="Name" error={errors.name?.message} {...register('name')} />

        {error !== null && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            <AlertCircle className="h-4 w-4" />
            Failed to rename API key. Please try again.
          </div>
        )}
      </form>
    </Modal>
  );
}

// Delete Confirmation Modal Component
function DeleteKeyModal({
  apiKey,
  onClose,
  onConfirm,
  isLoading,
}: {
  apiKey: AuthAPIKey;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}) {
  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={onClose}>
        Cancel
      </Button>
      <Button type="button" variant="danger" onClick={onConfirm} loading={isLoading}>
        Revoke Key
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Revoke API Key"
      subtitle="This action cannot be undone"
      size="md"
      footer={footerContent}
    >
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Are you sure you want to revoke{' '}
        <span className="font-medium text-gray-900 dark:text-white">{apiKey.name}</span>? Any
        applications using this key will no longer be able to authenticate.
      </p>
    </Modal>
  );
}
